import { RcraInfoFederalWasteGeneratorStatus } from "generated-graphql/graphql";
import { DateTime } from "luxon";

/**
 * The federal central-accumulation window, in days, by generator status.
 *
 * VSQG is absent on purpose rather than mapped to a number: a very small
 * quantity generator has no federal time limit at all, and inventing one for
 * the preview would show a countdown the saved container will not have.
 */
const CENTRAL_ACCUMULATION_DAYS: Partial<Record<RcraInfoFederalWasteGeneratorStatus, number>> = {
  [RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator]: 90,
  [RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator]: 180,
};

/** An SQG shipping over 200 miles to its designated facility gets 270 days. */
const SQG_OVER_200_MILES_DAYS = 270;

const GENERATOR_STATUS_ABBREVIATION: Partial<Record<RcraInfoFederalWasteGeneratorStatus, string>> =
  {
    [RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator]: "LQG",
    [RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator]: "SQG",
    [RcraInfoFederalWasteGeneratorStatus.VerySmallQuantityGenerator]: "VSQG",
  };

export type ClockProjection = {
  /** Length of the accumulation window. */
  totalDays: number;
  /** Whole days between today and the deadline; negative once it has passed. */
  daysRemaining: number;
  deadline: DateTime;
  /** "180 days — SQG, central accumulation". */
  basis: string;
};

/**
 * How long the container being drafted will have, so the form can show its
 * clock before the container exists.
 *
 * Deliberately a PREVIEW, not a second implementation of the clock: it reads
 * only the federal central-accumulation rule off the two inputs the form
 * already shows the user (the facility's generator status and the date the
 * container moves to central). The saved container's clock is computed by the
 * accumulation engine, which also applies state rules and satellite move
 * windows — so a state with a shorter window will show a nearer deadline on
 * the container than this projected here. Anything this can't state plainly
 * from those two inputs returns null and the rail says nothing rather than
 * guessing.
 */
export function projectCentralAccumulationClock({
  generatorStatus,
  movedToCentralDate,
  sqgOver200Miles,
  now = DateTime.local(),
}: {
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined;
  /** ISO date; the clock does not start until the container reaches central. */
  movedToCentralDate: string | null;
  sqgOver200Miles: boolean;
  now?: DateTime;
}): ClockProjection | null {
  if (!generatorStatus || !movedToCentralDate) {
    return null;
  }
  const baseDays = CENTRAL_ACCUMULATION_DAYS[generatorStatus];
  if (baseDays == null) {
    return null;
  }
  const isSqg = generatorStatus === RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator;
  const totalDays = isSqg && sqgOver200Miles ? SQG_OVER_200_MILES_DAYS : baseDays;

  const start = DateTime.fromISO(movedToCentralDate);
  if (!start.isValid) {
    return null;
  }
  const deadline = start.plus({ days: totalDays });
  // Day-granular, on calendar days rather than elapsed hours, so the number
  // matches the engine's own whole-day countdown.
  const daysRemaining = Math.round(deadline.startOf("day").diff(now.startOf("day"), "days").days);
  const abbreviation = GENERATOR_STATUS_ABBREVIATION[generatorStatus] ?? generatorStatus;

  return {
    totalDays,
    daysRemaining,
    deadline,
    basis: `${totalDays} days — ${abbreviation}, central accumulation`,
  };
}

/**
 * Whether the federal rule gives this generator status a central-accumulation
 * window at all.
 *
 * A VSQG has none, so no date the form is given will ever produce a
 * projection — which is a different thing to tell the reader than "you haven't
 * filled in the date yet".
 */
function hasCentralAccumulationLimit(
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined
): boolean {
  return generatorStatus != null && CENTRAL_ACCUMULATION_DAYS[generatorStatus] != null;
}

/**
 * What the form's clock card says when there is no projection to draw.
 *
 * Three different reasons produce no projection and they ask the reader for
 * different things: an unknown generator status is a gap in the facility's
 * data, a VSQG has no federal deadline to project at all, and a status that
 * does have a window is the only one of the three where filling in the
 * moved-to-central date will actually produce a dial.
 */
export function clockPlaceholderText(
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined,
  sqgOver200Miles: boolean
): string {
  const summary = generatorStatusSummary(generatorStatus, sqgOver200Miles);
  if (summary === null) {
    return "This facility's generator status isn't known, so no storage deadline can be projected.";
  }
  if (!hasCentralAccumulationLimit(generatorStatus)) {
    return `${summary}, so there's no deadline to project.`;
  }
  return "Set the moved-to-central date to project this container's deadline.";
}

/** "SQG — 180-day limit", the form's own label for the derived status. */
export function generatorStatusSummary(
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined,
  sqgOver200Miles: boolean
): string | null {
  if (!generatorStatus) {
    return null;
  }
  const abbreviation = GENERATOR_STATUS_ABBREVIATION[generatorStatus] ?? generatorStatus;
  const baseDays = CENTRAL_ACCUMULATION_DAYS[generatorStatus];
  if (baseDays == null) {
    return `${abbreviation} — no federal storage limit`;
  }
  const isSqg = generatorStatus === RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator;
  const days = isSqg && sqgOver200Miles ? SQG_OVER_200_MILES_DAYS : baseDays;
  return `${abbreviation} — ${days}-day limit`;
}
